import React, { useEffect, useState } from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    InputLabel,
    MenuItem,
    FormControl,
    Select,
    Button,
    Typography,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { TimekeeperService } from 'services/TimekeeperService';

const ConsiderOvertime = ({ open, setOpenTenerEnCuenta, rows, select, reloading }) => {
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const [elementos, setElementos] = useState([]);
    const [loading, setLoading] = useState(false);
    const tk = new TimekeeperService();

    // Función para identificar el tipo de elemento

    // Verificar si todos los elementos tienen el mismo valor de elegible
    const checkBiimplicacion = (lista) => {
        if (lista.length === 0) return true;
        const primerElemento = lista[0];
        return lista.every((elemento) => elemento.elegible === primerElemento.elegible);
    };

    useEffect(() => {
        if (open && select) {
            setLoading(true);
            const item = rows.find((item) => item.id === select.id);
            tk.combination_rule
                .getOvertimeCombination(item.id)
                .then((data) => {
                    // Crear una copia profunda para evitar mutaciones
                    const sobretiempos = item.overtime_rules.map((sobret) => {
                        // Crear elemento "Normal"
                        const sobretActual = {
                            ...sobret,
                            name: 'Normal',
                        };

                        // Filtrar otros elementos
                        const otrosSobret = item.overtime_rules.filter((elemt) => elemt.id !== sobret.id);

                        // Crear el array completo de elementos
                        const elementosArray = [sobretActual, ...otrosSobret, ...item.zones, ...item.schedule_desviation_rules].map((e) => ({
                            ...e,
                            elegible: false,
                        }));

                        // Retornar el sobret con sus elementos
                        return {
                            ...sobret,
                            elementos: elementosArray,
                            opcAvanzado: null,
                        };
                    });

                    if (data && data.length > 0) {
                        sobretiempos.forEach((sobret) => {
                            const overtimeRuleId = sobret.id;

                            sobret.elementos.forEach((elemento) => {
                                const tipo = getElementType(elemento);

                                // Obtener el ID correcto según el tipo de elemento
                                let elementId;
                                if (tipo === 'Overtime') {
                                    elementId = elemento.id;
                                } else if (tipo === 'Zone') {
                                    elementId = elemento.zone_id;
                                } else if (tipo === 'Schedule') {
                                    elementId = elemento.schedule_desviation_rule_id;
                                }

                                // Buscar el elemento correspondiente en data
                                const matchingElement = data.find((element) => element.type_element === tipo && element.id_element === elementId && element.overtime_rule_id === overtimeRuleId);

                                if (matchingElement) {
                                    elemento.elegible = matchingElement.elegible;
                                }
                            });
                        });
                    }

                    setElementos(sobretiempos);
                    setLoading(false);
                })
                .catch((error) => {
                    console.error('Error fetching overtime combination:', error);
                    setLoading(false);
                });
        }
        const getElementType = (elemento) => {
            if (elemento.hasOwnProperty('overtime_rule_id')) return 'Overtime';
            if (elemento.hasOwnProperty('zone_id')) return 'Zone';
            if (elemento.hasOwnProperty('schedule_desviation_rule_id')) return 'Schedule';
            return '';
        };
    }, [open, select, rows]);

    const handleChange = (overtime, elemento, event) => {
        setElementos((prevElementos) => {
            const newElementos = prevElementos.map((prevOvertime) => {
                if (prevOvertime.id !== overtime.id) return prevOvertime;

                const newOvertime = {
                    ...prevOvertime,
                    elementos: prevOvertime.elementos.map((elem) => {
                        if (elem.id !== elemento.id) return elem;
                        return { ...elem, elegible: event.target.value };
                    }),
                };

                // Verificar biimplicación y actualizar opcAvanzado si es necesario
                if (checkBiimplicacion(newOvertime.elementos)) {
                    newOvertime.opcAvanzado = null;
                }

                return newOvertime;
            });

            return newElementos;
        });
    };

    const closeDialog = () => {
        setOpenTenerEnCuenta(false);
        setLoading(false);
    };

    const assignConsiderOvertime = async () => {
        setLoading(true);
        try {
            const response = await tk.combination_rule.assignOvertimeCombination(select.id, elementos);

            if (response.status === 200) {
                enqueueSnackbar(t('settings.combinations.dialog.alerts.edit.success'), {
                    variant: 'success',
                });
                closeDialog();
                reloading();
            }
        } catch (error) {
            enqueueSnackbar(t('settings.combinations.dialog.alerts.edit.error'), {
                variant: 'error',
            });
            setLoading(false);
        }
    };

    return (
        <>
            <Dialog
                id="consider-overtime-dialog"
                open={open}
                onClose={() => setOpenTenerEnCuenta(false)}
                fullWidth
                maxWidth="md"
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">{t('settings.combinations.dialog.titles.consider_overtime')}</DialogTitle>
                <IconButton
                    id="consider-overtime-dialog"
                    onClick={() => closeDialog()}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                    }}
                >
                    <CloseIcon />
                </IconButton>
                <DialogContent>
                    <Typography variant="h6" sx={{ mb: 1.5 }}>
                        {t('settings.combinations.dialog.consider_overtime.name')}: {select?.name}
                    </Typography>

                    <Typography variant="body1">{t('settings.combinations.dialog.consider_overtime.info')}</Typography>
                    {elementos.map((overtime) => (
                        <React.Fragment key={overtime.overtime_rule_id}>
                            <TableContainer component={Paper}>
                                <Table stickyHeader sx={{ minWidth: 650 }} size="small" aria-label="simple table">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell colSpan={3}>
                                                <Typography variant="h4">
                                                    {overtime.name} {/* - {overtime.id} */}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell>{t('settings.combinations.dialog.consider_overtime.hours_worked')}</TableCell>
                                            <TableCell>{t('settings.combinations.dialog.consider_overtime.take_into_account')}</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {overtime.elementos.map((elemento) => (
                                            <TableRow
                                                key={`${elemento.overtime_rule_id}-${elemento.name}`}
                                                sx={{
                                                    '&:last-child td, &:last-child th': {
                                                        border: 0,
                                                    },
                                                }}
                                            >
                                                <TableCell component="th" scope="row">
                                                    {elemento.name} {/* - {elemento.id} */}
                                                </TableCell>
                                                <TableCell>
                                                    <FormControl fullWidth size="small">
                                                        <InputLabel id={`select-label-${elemento.overtime_rule_id}`}>
                                                            {t('settings.combinations.dialog.consider_overtime.take_into_account')}
                                                        </InputLabel>
                                                        <Select
                                                            labelId={`select-label-${elemento.overtime_rule_id}`}
                                                            id={`select-${elemento.overtime_rule_id}`}
                                                            value={elemento.elegible}
                                                            label={t('settings.combinations.dialog.consider_overtime.take_into_account')}
                                                            onChange={(event) => handleChange(overtime, elemento, event)}
                                                        >
                                                            <MenuItem value={true}>{t('settings.combinations.dialog.consider_overtime.yes')}</MenuItem>
                                                            <MenuItem value={false}>{t('settings.combinations.dialog.consider_overtime.no')}</MenuItem>
                                                        </Select>
                                                    </FormControl>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            <br />
                        </React.Fragment>
                    ))}
                </DialogContent>
                <DialogActions>
                    <Button variant="contained" color="error" onClick={() => closeDialog()}>
                        {t('settings.combinations.dialog.buttons.cancel')}
                    </Button>
                    <Button onClick={() => assignConsiderOvertime()} variant="contained" color="primary" loading={loading}>
                        {t('settings.combinations.dialog.buttons.save')}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export { ConsiderOvertime };
